import {Dropdown} from "primereact/dropdown"
import {MultiSelect} from "primereact/multiselect"
import {useState, useContext, useEffect, useRef} from "react"
import {Form, Button, Spinner, Modal} from "react-bootstrap"
import useSWR, {mutate} from "swr"
import {useBlocker} from "react-router-dom"

import {fetcherGet} from "../../utilities/general"
import RefContext from "../../utilities/RefContext"
import {wrappedGet, wrappedPost, wrappedPut} from "../../utilities/AxiosHelper"
import RoleList from "./RoleList"

const EditUser = (props) => {
    const role = props.role

    const [user, setUser] = useState({...props.user})
    const [emailError, setEmailError] = useState("")
    const [employeeIDError, setEmployeeIDError] = useState("")
    const [roleError, setRoleError] = useState("")
    const [isFacilityLoading, setIsFacilityLoading] = useState(false)
    const [isUnitLoading, setIsUnitLoading] = useState(false)
    const [facilities, setFacilities] = useState([])
    const [units, setUnits] = useState([])
    const [unitIds, setUnitIds] = useState(user?.units?.map((unit) => unit.id) || [])

    const [isUserEdited, setIsUserEdited] = useState(false)

    const bypassRef = useRef(false)
    const blocker = useBlocker(() => isUserEdited && !bypassRef.current)

    const {dispatch} = useContext(RefContext)

    const add = !!props.add

    const changeValues = (values) => {
        let newUser = {...user}
        for (const [key, value] of Object.entries(values)) {
            newUser[key] = value
        }

        if (newUser.role_id === 1) {
            delete newUser.organization_id
            delete newUser.facility_id
            delete newUser.unit_ids
            setUnitIds([])
        }

        setUser(newUser)
        setIsUserEdited(true)
    }

    const {data: organizations} = useSWR("/get-organizations", fetcherGet, {fallbackData: []})
    const {data: roles} = useSWR("/get-roles", fetcherGet, {fallbackData: []})

    useEffect(() => {
        if (!role || isUserEdited) return

        setUser({...props.user})
        setUnitIds(props.user?.units?.map((unit) => unit.id).filter(Boolean) ?? [])

        if (!add) {
            Promise.all([
                fetchFacilities(props.user.organization_id),
                fetchUnits(props.user.organization_id, props.user.facility_id),
            ])
        }
    }, [add, props.user])

    // Functions to prevent users from navigating away if they have unsaved changes
    const leaveNow = () => {
        bypassRef.current = true
        props.onDone()
    }

    const handleSubmit = (event) => {
        event.preventDefault()
        saveUser().then((ok) => ok && leaveNow())
    }

    const handleSaveAndClose = () => {
        saveUser().then((ok) => {
            return ok ? blocker.proceed() : blocker.reset()
        })
    }

    const saveUser = (event) => {
        event?.preventDefault()

        setRoleError("")
        setEmailError("")

        let form_error = false
        if (!user.email || user.email.trim() === "") {
            setEmailError("Email is required")
            form_error = true
        }
        if (!user.role_name || user.role_name.trim() === "") {
            setRoleError("Role is required")
            form_error = true
        }
        if (!user.employee_id || user.employee_id.trim() === "") {
            setEmployeeIDError("Employee ID is required")
            form_error = true
        }

        if (form_error) return Promise.resolve(false)

        let func = wrappedPost
        let url = "users"
        if (!add) {
            func = wrappedPut
            url = `users/${user.id}`
        }

        let saveUserForm = {}
        saveUserForm = {
            account: user.account,
            role_id: user.role_id,
            first_name: user.first_name,
            last_name: user.last_name,
            password: user.password,
            email: user.email,
            active: user.active,
            employee_id: user.employee_id,
            user_type_id: user.user_type_id,
            organization_id: user.organization_id,
            facility_id: user.facility_id,
            unit_ids: unitIds,
            allow_osrs: user.allow_osrs,
        }

        return func(url, saveUserForm)
            .then(() => {
                mutate("/users")
                setIsUserEdited(false)
                return true
            })
            .catch((error) => {
                const errorMessage = error.response?.data?.["Error"]
                dispatch("showToast", {
                    show: true,
                    variant: "danger",
                    text: errorMessage || "Error saving user",
                    timeout: 5000,
                })
                return false
            })
    }

    const changeUnitIds = (ids) => {
        setUnitIds(ids)
        setIsUserEdited(true)
    }

    if (!role) return null

    const renderAccountNameFields = (user) => {
        return (
            <div className="row">
                <div className="fv-row col-6 mb-7 fv-plugins-icon-container">
                    <Form.Label className="required">First Name</Form.Label>
                    <Form.Control
                        required
                        type="text"
                        name="first_name"
                        placeholder="First name"
                        value={user.first_name}
                        onChange={(evt) => changeValues({first_name: evt.target.value})}
                    />
                </div>
                <div className="fv-row col-6 mb-7 fv-plugins-icon-container">
                    <Form.Label className="required">Last Name</Form.Label>
                    <Form.Control
                        required
                        type="text"
                        name="last_name"
                        placeholder="Last name"
                        value={user.last_name}
                        onChange={(evt) => changeValues({last_name: evt.target.value})}
                    />
                </div>
                <div className="fv-row col-6 mb-7 fv-plugins-icon-container">
                    <Form.Label className="required">Account</Form.Label>
                    <Form.Control
                        required
                        type="text"
                        name="account"
                        placeholder="Account"
                        value={user.account}
                        onChange={(evt) => changeValues({account: evt.target.value})}
                    />
                </div>
            </div>
        )
    }

    const renderEmailEmployeeIDFields = (user) => {
        return (
            <div className="row d-flex align-items-end">
                <div className="fv-row mb-7 fv-plugins-icon-container col-6">
                    <Form.Label className="required">Email</Form.Label>
                    <Form.Control
                        type="email"
                        name="email"
                        placeholder="Email"
                        value={user.email}
                        onChange={(evt) => changeValues({email: evt.target.value})}
                        required={true}
                    />
                    <div className="fv-plugins-message-container fv-plugins-message-container--enabled invalid-feedback">
                        {emailError}
                    </div>
                </div>
                <div className="fv-row mb-7 fv-plugins-icon-container col-6">
                    <Form.Label className="required">Employee ID</Form.Label>
                    <Form.Control
                        type="employee_id"
                        name="employee_id"
                        placeholder="Employee ID"
                        value={user.employee_id}
                        onChange={(evt) => changeValues({employee_id: evt.target.value})}
                        required={true}
                    />
                    <div className="fv-plugins-message-container fv-plugins-message-container--enabled invalid-feedback">
                        {employeeIDError}
                    </div>
                </div>
            </div>
        )
    }

    const fetchFacilities = (organizationId) => {
        if (!organizationId) {
            setFacilities([])
            return Promise.resolve()
        }
        setIsFacilityLoading(true)
        return wrappedGet(`organization/${organizationId}/facility`)
            .then((response) => {
                setFacilities(response.data.results)
            })
            .catch((error) => {
                dispatch("showToast", {
                    show: true,
                    variant: "danger",
                    text: "Problem pulling Facilities",
                    timeout: 5000,
                })
            })
            .finally(() => {
                setIsFacilityLoading(false)
            })
    }

    const fetchUnits = (organizationId, facilityId) => {
        if (!organizationId || !facilityId) {
            setUnits([])
            return Promise.resolve()
        }
        setIsUnitLoading(true)
        return wrappedGet(`/organization/${organizationId}/facility/${facilityId}/unit`)
            .then((response) => {
                setUnits(response.data.results)
            })
            .catch(() => {
                dispatch("showToast", {
                    show: true,
                    variant: "danger",
                    text: "Problem pulling Units",
                    timeout: 5000,
                })
            })
            .finally(() => {
                setIsUnitLoading(false)
            })
    }

    const renderOrganizationFacilityUnitFields = (user) => {
        if (user.role_id === 1 && user.role_name === "Enterprise Admin") {
            return null
        }
        return (
            <>
                {role === "Enterprise Admin" && (
                    <div className="row d-flex align-items-end">
                        <div className="fv-row mb-7 fv-plugins-icon-container col-6">
                            <Form.Group controlId="organization_id">
                                <Form.Label>Organization</Form.Label>
                                {add ? (
                                    <Dropdown
                                        className="w-100 p-dropdown-tall"
                                        panelClassName="show-10"
                                        showClear={true}
                                        name="organization_id"
                                        value={user.organization_id}
                                        options={organizations.map((org) => {
                                            return {label: org.name, value: org.id}
                                        })}
                                        filter={false}
                                        onChange={(evt) => {
                                            changeValues({organization_id: evt.target.value})
                                            // get facilities
                                            fetchFacilities(evt.target.value)
                                        }}
                                    />
                                ) : (
                                    <span className="ms-4">{user.organization_name}</span>
                                )}
                            </Form.Group>
                        </div>
                    </div>
                )}
                <div className="row d-flex align-items-end">
                    <div className="fv-row mb-10 fv-plugins-icon-container col-6">
                        {isFacilityLoading && (
                            <div className="d-flex align-items-center mg-1">
                                <span className="me-2">Finding Facilities...</span>
                                <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
                            </div>
                        )}
                        {!isFacilityLoading && (
                            <Form.Group controlId="facility_id">
                                <Form.Label>Facility</Form.Label>
                                <Dropdown
                                    className="w-100 p-dropdown-tall"
                                    panelClassName="show-10"
                                    showClear={true}
                                    name="facility_id"
                                    value={user.facility_id}
                                    options={facilities.map((facility) => {
                                        return {label: facility.name, value: facility.id}
                                    })}
                                    filter={false}
                                    onChange={(evt) => {
                                        changeValues({facility_id: evt.target.value})
                                        changeUnitIds([])
                                        fetchUnits(user.organization_id, evt.target.value)
                                    }}
                                />
                            </Form.Group>
                        )}
                    </div>
                    <div className="fv-row mb-10 fv-plugins-icon-container col-6">
                        {isUnitLoading && (
                            <div className="d-flex align-items-center mt-1">
                                <span className="me-2">Finding Units...</span>
                                <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
                            </div>
                        )}
                        {!isUnitLoading && user.facility_id && user.organization_id && (
                            <Form.Group controlId="unitIds">
                                <Form.Label>Unit</Form.Label>
                                <MultiSelect
                                    className="w-100 p-dropdown-tall"
                                    panelClassName="show-10"
                                    showClear={true}
                                    name="unitIds"
                                    value={unitIds}
                                    options={units.map((unit) => {
                                        return {label: unit.name, value: unit.id}
                                    })}
                                    filter={false}
                                    onChange={(evt) => {
                                        changeUnitIds(evt.target.value)
                                    }}
                                />
                            </Form.Group>
                        )}
                    </div>
                </div>
            </>
        )
    }

    const isDisabled = () => {
        const hasMissingRequiredFields =
            !user.first_name ||
            !user.last_name ||
            !user.email ||
            !user.employee_id ||
            !user.account ||
            !user.role_id

        if (add) {
            return hasMissingRequiredFields
        }

        return !isUserEdited
    }

    return (
        <>
            {blocker.state === "blocked" && (
                <Modal show centered onHide={() => blocker.reset()} animation={false}>
                    <Modal.Header closeButton closeVariant="white" className="modal-header">
                        <Modal.Title>Save your changes?</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>You have unsaved changes on this page.</Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={() => blocker.proceed()}>
                            Do Not Save
                        </Button>
                        <Button variant="primary" onClick={handleSaveAndClose}>
                            Save and Close
                        </Button>
                    </Modal.Footer>
                </Modal>
            )}
            <div className="card">
                <Form onSubmit={handleSubmit} noValidate>
                    <div className="card-body p-10">
                            <>
                                <h4 className="fw-bold text-gray-800 mb-6">User Details</h4>
                                {renderAccountNameFields(user)}
                                {renderEmailEmployeeIDFields(user)}
                                {!add && (
                                    <div className="form-check form-switch form-check-custom form-check-success form-check-solid mb-3">
                                        <input
                                            className="form-check-input"
                                            type="checkbox"
                                            id="id-flex-switch-checked"
                                            checked={user.active}
                                            onChange={() => changeValues({active: !user.active})}
                                        />
                                        <label
                                            className="form-check-label fw-bold fs-5"
                                            htmlFor="id-flex-switch-checked"
                                        >
                                            Account Active
                                        </label>
                                    </div>
                                )}

                                {!add && user.role_id !== 1 && (
                                    <div className="form-check form-switch form-check-custom form-check-success form-check-solid mb-3">
                                        <input
                                            className="form-check-input"
                                            type="checkbox"
                                            id="id-flex-switch-checked"
                                            checked={user.allow_osrs}
                                            onChange={() => changeValues({allow_osrs: !user.allow_osrs})}
                                        />
                                        <label
                                            className="form-check-label fw-bold fs-5"
                                            htmlFor="id-flex-switch-checked"
                                        >
                                            Allow OSRs
                                        </label>
                                    </div>
                                )}

                                <div className="separator my-10"></div>
                                <h4 className="fw-bold text-gray-800 mb-6">Role &amp; Assignment</h4>
                                <div className="row">
                                    <div className="col-6">
                                        <Form.Group controlId="role_id" className="mb-7">
                                            <Form.Label className="required">Role</Form.Label>
                                            <RoleList
                                                changeValues={changeValues}
                                                role_id={user.role_id}
                                                fieldError={roleError}
                                                roles={roles}
                                                userRole={props.role}
                                                roleOptions={roles}
                                            />
                                        </Form.Group>
                                    </div>
                                    <div className="col-6">
                                        <Form.Group controlId="user_type_id">
                                            <Form.Label>User Type</Form.Label>
                                            <Dropdown
                                                className="w-100 p-dropdown-tall"
                                                panelClassName="show-10"
                                                showClear={false}
                                                name="user_type_id"
                                                value={user.user_type_id}
                                                options={[
                                                    {label: "RN", value: 1},
                                                    {label: "LPN", value: 2},
                                                ]}
                                                filter={false}
                                                onChange={(evt) => {
                                                    changeValues({user_type_id: evt.target.value})
                                                }}
                                            />
                                        </Form.Group>
                                    </div>
                                </div>
                                {renderOrganizationFacilityUnitFields(user)}
                            </>
                    </div>

                    <div className="card-footer d-flex justify-content-end gap-3 py-6">
                        <Button variant="primary" type="submit" disabled={isDisabled()}>
                            {add ? "Create Account" : "Save Changes"}
                        </Button>
                    </div>
                </Form>
            </div>
        </>
    )
}

export default EditUser
